import {
  addColumnsToTable,
  addPapersToTable,
  removeColumnsFromTable,
  removePapersFromTable,
  resetTable,
} from '@/store/reducers/TableWidgetStore';
import { AppDispatch } from '@/store/store';
import { fetchReportStateByWidgetId } from '@/store/thunks/FetchReport';
import { fetchTableStateByWidgetId } from '@/store/thunks/FetchTable';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import { IntermediateMessage, ToolCall } from '@/api/ApiResponse';
import { NewTableColumn, tableColumnFactory } from '@/models/Table';
import {
  openReaderWidget,
  openReportWidget,
  openTableWidget,
} from '@/store/actionCreators/WidgetActions';
import { ThreadId } from '@/models/Thread';

enum UITools {
  UI_READER_OPEN_PAPER = 'ui_reader_open_paper',
  UI_READER_SCROLL_TO_PAGE = 'ui_reader_scroll_to_page',
  UI_TABLE_ADD_COLUMN = 'ui_table_add_aspect',
  UI_TABLE_REMOVE_COLUMN = 'ui_table_remove_aspect',
  UI_TABLE_ADD_PAPERS = 'ui_table_add_papers',
  UI_TABLE_REMOVE_PAPERS = 'ui_table_remove_papers',
}

enum WidgetTools {
  'TABLE_WIDGET_ADD_PAPERS' = 'add_papers_to_table_widget',
  'TABLE_WIDGET_REMOVE_ROWS' = 'remove_papers_from_table_widget',
  'TABLE_WIDGET_ADD_COLUMNS' = 'add_aspects_to_table_widget',
  'TABLE_WIDGET_REMOVE_COLUMNS' = 'remove_aspects_from_table_widget',
  'REPORT_WIDGET_CREATE' = 'report_widget_create',
  'CORPUS_QA' = 'corpus_qa',
}

export async function handleOpenReportWidget({
  currentThreadId,
  dispatch,
}: {
  currentThreadId: ThreadId;
  dispatch: AppDispatch;
}) {
  dispatch(openReportWidget({ currentThreadId, reportData: null }));
}

async function handleUIReaderOpenPaper({
  currentThreadId,
  corpusId,
  dispatch,
}: {
  currentThreadId: ThreadId;
  corpusId: number;
  dispatch: AppDispatch;
}) {
  dispatch(openReaderWidget({ currentThreadId, corpusId }));
  // we can add an async check here to see if the reader has opened successfully
}

async function handleUIReaderScrollToPage({
  currentThreadId,
  pageNumber,
  corpusId,
  dispatch,
}: {
  currentThreadId: ThreadId;
  pageNumber: number;
  corpusId: number;
  dispatch: AppDispatch;
}) {
  const bboxForPage = {
    page: Math.max(0, pageNumber - 1),
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  };
  dispatch(
    openReaderWidget({ currentThreadId, corpusId, highlights: [bboxForPage] }),
  );
  // we can add an async check here to see if the reader has opened successfully
}

export interface TableToolPaper {
  corpusId: number;
  title?: string;
}

export async function handleUIRemovePapersFromTable({
  currentThreadId,
  dispatch,
  corpusIds,
}: {
  currentThreadId: ThreadId;
  dispatch: AppDispatch;
  corpusIds: number[];
}) {
  const papers: TableToolPaper[] = corpusIds.map((corpusId) => ({ corpusId }));
  dispatch(removePapersFromTable({ currentThreadId, papers }));
}

export async function handleUIRemoveColumnFromTable({
  currentThreadId,
  dispatch,
  columnId,
}: {
  currentThreadId: ThreadId;
  dispatch: AppDispatch;
  columnId: string;
}) {
  dispatch(removeColumnsFromTable({ currentThreadId, columnIds: [columnId] }));
}

export async function handleUIResetTable({
  currentThreadId,
  dispatch,
}: {
  currentThreadId: ThreadId;
  dispatch: AppDispatch;
}) {
  dispatch(resetTable(currentThreadId));
}

export async function handleFetchLatestTableState({
  currentThreadId,
  dispatch,
}: {
  currentThreadId: ThreadId;
  dispatch: AppDispatch;
}) {
  setTimeout(() => {
    dispatch(
      fetchTableStateByWidgetId({
        threadId: currentThreadId,
        widgetId: currentThreadId, // rely on the widget id being the same as the table id for now
      }),
    );
  }, 1500); // HACK: wait for the backend to update the table state
}

export async function handleFetchReportState({
  currentThreadId,
  dispatch,
  reportId,
}: {
  currentThreadId: ThreadId;
  dispatch: AppDispatch;
  reportId: string;
}) {
  dispatch(
    fetchReportStateByWidgetId({
      threadId: currentThreadId,
      widgetId: reportId,
    }),
  );
}

export async function processUIToolCalls({
  currentThreadId,
  tools,
  dispatch,
}: {
  currentThreadId: ThreadId;
  tools: IntermediateMessage[];
  dispatch: AppDispatch;
}) {
  const uiTools = tools.filter(
    (msg) => msg.kwargs.type === 'tool' && msg.kwargs.name?.startsWith('ui_'),
  );

  handleWidgetToolCalls({ currentThreadId, tools, dispatch });

  for (const msg of uiTools) {
    const toolName = msg.kwargs.name;
    const output = JSON.parse(msg.kwargs.content);
    try {
      switch (toolName) {
        case UITools.UI_READER_OPEN_PAPER: {
          const corpusId = parseInt(`${output?.corpus_id}`);
          await handleUIReaderOpenPaper({
            currentThreadId,
            corpusId,
            dispatch,
          });
          break;
        }
        case UITools.UI_READER_SCROLL_TO_PAGE: {
          const corpusId = parseInt(`${output?.corpus_id}`);
          const pageNumber = parseInt(`${output?.page_number}`);
          await handleUIReaderScrollToPage({
            currentThreadId,
            pageNumber,
            corpusId,
            dispatch,
          });
          break;
        }
        case UITools.UI_TABLE_REMOVE_PAPERS: {
          const corpusIds = output?.corpusIds;
          await handleUIRemovePapersFromTable({
            currentThreadId,
            dispatch,
            corpusIds,
          });
          break;
        }
        case UITools.UI_TABLE_REMOVE_COLUMN: {
          const columnId = output?.columnId;
          await handleUIRemoveColumnFromTable({
            currentThreadId,
            dispatch,
            columnId,
          });
          break;
        }
        default: {
          throw new Error(`Unknown ui tool: ${toolName}`);
        }
      }
    } catch (error: any) {
      console.error('Failed to process ui tool', toolName, output, error);
      getLayoverLogger().logError(
        `Failed to process ui tool ${toolName} ${output} ${error.stack}`,
        error,
      );
    }
  }
}

// HACK: this function enables some optimistic rendering for table widgets once we know a widget tool is called with given args
// this should be replaced with pub sub or some progress indicator mechanism in the future
async function handleWidgetToolCalls({
  currentThreadId,
  tools,
  dispatch,
}: {
  currentThreadId: ThreadId;
  tools: IntermediateMessage[];
  dispatch: AppDispatch;
}) {
  const widgetToolsSet = new Set(Object.values(WidgetTools) as string[]);

  const widgetToolCalls: ToolCall[] = [];
  tools.forEach((msg) => {
    // eslint-disable-next-line camelcase
    const { type, tool_calls } = msg.kwargs;

    // eslint-disable-next-line camelcase
    if (type === 'ai' && tool_calls) {
      // eslint-disable-next-line camelcase
      tool_calls.forEach((call) => {
        if (widgetToolsSet.has(call.name)) {
          widgetToolCalls.push(call);
        }
      });
    }
  });

  for (const call of widgetToolCalls) {
    const { name, args } = call;
    switch (name) {
      case WidgetTools.REPORT_WIDGET_CREATE:
      case WidgetTools.CORPUS_QA:
        await handleOpenReportWidget({
          currentThreadId,
          dispatch,
        });
        break;
      case WidgetTools.TABLE_WIDGET_ADD_COLUMNS: {
        const { columns } = args;
        handleAddTableColumns({ currentThreadId, columns, dispatch });
        break;
      }
      case WidgetTools.TABLE_WIDGET_ADD_PAPERS: {
        // eslint-disable-next-line camelcase
        const { paper_corpus_ids } = args;
        handleAddTableRows({
          currentThreadId,
          // eslint-disable-next-line camelcase
          corpusIds: paper_corpus_ids,
          dispatch,
        });
        break;
      }
      case WidgetTools.TABLE_WIDGET_REMOVE_ROWS:
      case WidgetTools.TABLE_WIDGET_REMOVE_COLUMNS: {
        handleFetchLatestTableState({
          currentThreadId,
          dispatch,
        });
        break;
      }
      default: {
        console.error('Unknown widget tool:', name);
      }
    }
  }
}

function handleAddTableColumns({
  currentThreadId,
  columns,
  dispatch,
}: {
  currentThreadId: ThreadId;
  columns: NewTableColumn[];
  dispatch: AppDispatch;
}) {
  dispatch(openTableWidget({ currentThreadId }));

  // optimistically add column to table
  const optimisticColumns = columns.map((column) =>
    tableColumnFactory({ name: column.name, description: column.description }),
  );

  dispatch(addColumnsToTable({ currentThreadId, columns: optimisticColumns }));

  handleFetchLatestTableState({
    currentThreadId,
    dispatch,
  });
}

function handleAddTableRows({
  currentThreadId,
  corpusIds,
  dispatch,
}: {
  currentThreadId: ThreadId;
  corpusIds: number[];
  dispatch: AppDispatch;
}) {
  dispatch(openTableWidget({ currentThreadId }));

  // optimistically add papers to table
  const papers: TableToolPaper[] = corpusIds.map((corpusId) => ({
    corpusId,
  }));
  dispatch(addPapersToTable({ currentThreadId, papers }));

  handleFetchLatestTableState({
    currentThreadId,
    dispatch,
  });
}
